import React, { useEffect } from 'react';
import { Switch } from 'react-router-dom';
import RouterSuspense from '~/app/common/RouterSuspense';
import { Route, useHistory, useParams } from "react-router";
import Details from '.';
import PrivateRoute from '~/app/PrivateRoute';
import { PERMISSION_GROUPS } from '~/accessControl/permissionGroups';
import PeriodicCheck from '~/periodicCheck';
import Issues from '~/issues/List';
import Documentation from '~/documentation';
import IssueDetails from "~/issues/Details";
import { useStoreActions, useStoreState } from '~/store';
import { Machine, Project } from '~/types';
import { MACHINES_ROUTES } from '~/machines/Router';
import { ROUTES } from '~/app/Router';
import { route } from '~/app/helpers/route';
import usePageTitle from '~/app/hooks/usePageTitle';
import { MachineTabs } from '~/app/data/statuses/machine';
import Services from '~/services/List';
import ServiceDetail from '~/services/Details';
import AccessDenied from '~/accessControl/AccessDenied';
import ServiceChecklist from '~/services/Details/ServiceChecklist';
import Trainings from '~/training';
import TrainingForm from '~/training/Add';
import Equipments from '~/periodicCheck/Equipments';
import Communication from '~/communication';
import CommunicationRoleUsers from '~/communication/RoleUsers';
import CommunicationChat from '~/communication/Chat';

export const MACHINES_DETAILS_ROUTES = {
    details: '/projects/:projectId/machines/:id',
    periodicCheck: '/projects/:projectId/machines/:id/periodic-check',
    periodicCheckEquipments: '/projects/:projectId/machines/:id/periodic-check/equipments',
    documentation: '/projects/:projectId/machines/:id/documentation/:equipmentId?',
    communication: '/projects/:projectId/machines/:id/communication',
    communicationRoleUsers: '/projects/:projectId/machines/:id/communication/roles/:roleId/users',
    communicationChat: '/projects/:projectId/machines/:id/communication/chat',
    machineCommunicationChat: '/machines/:id/communication/chat',
    issues: '/machines/:id/issues',
    openCriticalIssues: '/machines/:id/open-critical-issues',
    issueDetails: '/machines/:id/issues/:issueId',
    services: '/machines/:id/services',
    serviceDetails: '/machines/:id/services/:serviceId',
    serviceCheck: '/machines/:id/services/:serviceId/service-check',
    registerTraining: '/machines/:id/training/add',
    listTraining: '/machines/:id/training',
    timeManagementStart:'/timeManagement/startTime'
};

export type RouterComponent = React.FC

const Router: RouterComponent = () => {
    const history = useHistory();
    const netWorkStatus = useStoreState(state => state.app.offline.status);
    const {id, projectId} = useParams<{ id: Machine['id'], projectId: Project['id'] }>();
    const machine = useStoreState(state => state.machine.data);
    const updateLastUsedAction = useStoreActions(actions => actions.machines.updateLastUsed);
    const MachineFetchAction = useStoreActions(actions => actions.machine.fetchMachine);
    const OfflineMachineFetchAction = useStoreActions(actions => actions.machine.fetchOfflineMachine);
    const MachineDashboardFetchAction = useStoreActions(actions => actions.machine.fetchMachineDashboard);
    const OfflineMachineDashbaordFetchAction = useStoreActions(actions => actions.machine.fetchOfflineMachineDashboard);
    const inFavorite = useStoreState(state => state.machines.inFavorite(id));
    const activeTab = useStoreState(state => state.machine.activeTab);

    usePageTitle((machine.name || '').toUpperCase());

    useEffect(() => {
        if ((netWorkStatus === 0 || inFavorite) && id) {
            (netWorkStatus ? (activeTab === MachineTabs.DASHBOARD ? OfflineMachineDashbaordFetchAction : OfflineMachineFetchAction) : (activeTab === MachineTabs.DASHBOARD ? MachineDashboardFetchAction : MachineFetchAction))(id).then(() => {
                netWorkStatus === 0 && updateLastUsedAction({ machineId: id });
            });
        } else {
            history.push(projectId ? route(MACHINES_ROUTES.machines, { projectId }) : route(ROUTES.projects));
        }
    }, [MachineFetchAction, id, projectId, netWorkStatus, OfflineMachineFetchAction, updateLastUsedAction, inFavorite, history, MachineDashboardFetchAction, activeTab, OfflineMachineDashbaordFetchAction]);

    if (machine.id && !machine.hasAccessToCompany) {
        return <AccessDenied type={2} />
    }

    return (
        <RouterSuspense>
            <Switch>
                <PrivateRoute path={MACHINES_DETAILS_ROUTES.periodicCheckEquipments} component={Equipments} requiredPermissions={PERMISSION_GROUPS.readWritePeriodicCheck} />
                <PrivateRoute path={MACHINES_DETAILS_ROUTES.periodicCheck} component={PeriodicCheck} requiredPermissions={PERMISSION_GROUPS.readWritePeriodicCheck}/>
                <PrivateRoute path={MACHINES_DETAILS_ROUTES.documentation} component={Documentation} requiredPermissions={PERMISSION_GROUPS.readMachineDocuments}/>
                <PrivateRoute path={MACHINES_DETAILS_ROUTES.machineCommunicationChat} component={CommunicationChat} requiredPermissions={PERMISSION_GROUPS.machineRegistration}/>
                <PrivateRoute path={MACHINES_DETAILS_ROUTES.communicationChat} component={CommunicationChat} requiredPermissions={PERMISSION_GROUPS.machineRegistration}/>
                <PrivateRoute path={MACHINES_DETAILS_ROUTES.communicationRoleUsers} component={CommunicationRoleUsers} requiredPermissions={PERMISSION_GROUPS.machineRegistration}/>
                <PrivateRoute path={MACHINES_DETAILS_ROUTES.communication} component={Communication} requiredPermissions={PERMISSION_GROUPS.machineRegistration}/>
                <PrivateRoute path={MACHINES_DETAILS_ROUTES.issueDetails} component={IssueDetails} requiredPermissions={PERMISSION_GROUPS.readIssues}/>
                <PrivateRoute path={MACHINES_DETAILS_ROUTES.serviceCheck} component={ServiceChecklist} requiredPermissions={PERMISSION_GROUPS.readServiceDetails}/>
                <PrivateRoute path={MACHINES_DETAILS_ROUTES.serviceDetails} component={ServiceDetail} requiredPermissions={PERMISSION_GROUPS.readServiceDetails}/>
                <PrivateRoute path={MACHINES_DETAILS_ROUTES.issues} component={Issues} requiredPermissions={PERMISSION_GROUPS.readIssues}/>
                <PrivateRoute path={MACHINES_DETAILS_ROUTES.services} component={Services} requiredPermissions={PERMISSION_GROUPS.readServiceDetails}/>
                <PrivateRoute path={MACHINES_DETAILS_ROUTES.openCriticalIssues} component={Issues} requiredPermissions={PERMISSION_GROUPS.readIssues}/>
                <PrivateRoute path={MACHINES_DETAILS_ROUTES.registerTraining} component={TrainingForm} requiredPermissions={PERMISSION_GROUPS.registerTraining}/>
                <PrivateRoute path={MACHINES_DETAILS_ROUTES.listTraining} component={Trainings} requiredPermissions={PERMISSION_GROUPS.training} />
                <Route path={MACHINES_DETAILS_ROUTES.details} component={Details} />
            </Switch>
        </RouterSuspense>
    )
};

export default Router;
